import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { adminUpdateUserProfile } from '../../util/api';
import { setCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getListingApi } from '../EditListingPage/listingApi';

// ================ Async Thunks ================ //

//////////////////
// Upload Image //
//////////////////
// `listingApi` (see EditListingPage/listingApi.js) is set by the admin listing editor so
// the photo is uploaded through the admin endpoints; by default the SDK is used.
export const uploadImageThunk = createAsyncThunk(
  'ProfileSettingsPage/uploadImage',
  ({ id, file, listingApi }, { rejectWithValue, extra: sdk }) => {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return getListingApi(listingApi, sdk)
      .uploadImage(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        return { id, uploadedImage };
      })
      .catch(e => {
        return rejectWithValue({ id, error: storableError(e) });
      });
  }
);
// Backward compatible wrapper for the uploadImage thunk
export const uploadImage = (actionPayload, listingApi) => dispatch => {
  return dispatch(uploadImageThunk({ ...actionPayload, listingApi }));
};

////////////////////
// Update Profile //
////////////////////
export const updateProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

// Admin listing editor: the same profile update for another user (the listing author),
// through the admin endpoint. The updated user is merged into marketplaceData, which is
// where the editor reads the listing author from; the admin's own currentUser is untouched.
export const updateUserProfileThunk = createAsyncThunk(
  'ProfileSettingsPage/updateUserProfile',
  ({ userId, profile }, { dispatch, rejectWithValue }) => {
    const userIdString = userId && userId.uuid ? userId.uuid : userId;
    return adminUpdateUserProfile(userIdString, profile)
      .then(response => {
        dispatch(addMarketplaceEntities(response));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
export const updateUserProfile = (userId, profile) => dispatch => {
  return dispatch(updateUserProfileThunk({ userId, profile }));
};

// ================ Slice ================ //

const profileSettingsPageSlice = createSlice({
  name: 'ProfileSettingsPage',
  initialState: {
    image: null,
    uploadImageError: null,
    uploadInProgress: false,
    updateInProgress: false,
    updateProfileError: null,
  },
  reducers: {
    clearUpdatedForm: state => {
      state.updateProfileError = null;
      state.uploadImageError = null;
    },
    // Drop an uploaded-but-unsaved photo (e.g. when the admin editor opens another expert).
    clearImage: state => {
      state.image = null;
      state.uploadInProgress = false;
      state.uploadImageError = null;
    },
  },
  extraReducers: builder => {
    builder
      // uploadImage cases
      .addCase(uploadImageThunk.pending, (state, action) => {
        const { id, file } = action.meta.arg;
        state.image = { id, file };
        state.uploadInProgress = true;
        state.uploadImageError = null;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action) => {
        const { id, uploadedImage } = action.payload;
        const { file } = state.image || {};
        state.image = { id, imageId: uploadedImage.id, file, uploadedImage };
        state.uploadInProgress = false;
      })
      .addCase(uploadImageThunk.rejected, (state, action) => {
        state.image = null;
        state.uploadInProgress = false;
        state.uploadImageError = action.payload.error;
      })
      // updateProfile / updateUserProfile cases (same progress and error handling)
      .addMatcher(isAnyOf(updateProfileThunk.pending, updateUserProfileThunk.pending), state => {
        state.updateInProgress = true;
        state.updateProfileError = null;
      })
      .addMatcher(
        isAnyOf(updateProfileThunk.fulfilled, updateUserProfileThunk.fulfilled),
        state => {
          state.image = null;
          state.updateInProgress = false;
        }
      )
      .addMatcher(
        isAnyOf(updateProfileThunk.rejected, updateUserProfileThunk.rejected),
        (state, action) => {
          state.image = null;
          state.updateInProgress = false;
          state.updateProfileError = action.payload;
        }
      );
  },
});

export const { clearUpdatedForm, clearImage } = profileSettingsPageSlice.actions;
export default profileSettingsPageSlice.reducer;
